<template>
  <div class="space-y-10 divide-y divide-gray-900/10">
    <div class="grid grid-cols-1 gap-x-8 gap-y-8 pt-10 md:grid-cols-3">
      <div class="px-4 sm:px-0">
        <h2 class="text-base font-semibold leading-7 text-gray-900">Business Function</h2>
        <p class="mt-1 text-sm leading-6 text-gray-600">Business Function Text Prompt</p>
      </div>

      <Form v-slot="{ handleSubmit, errors}" :validation-schema="schema" class="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
        <button v-if="questionSectionId" @click="destroy" type="button" class="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 m-3">
          <TrashIcon class="h-5 w-5" aria-hidden="true" />
        </button>
        <div class="px-4 py-6 sm:p-8">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">

            <div class="sm:col-span-5">
              <label for="title" class="block text-sm font-medium leading-6 text-gray-900">
                Title
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field v-model="formData.title" v-bind:class="{ 'ring-red-600': errors.title }" type="text" name="title" id="title" placeholder="Title" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="title" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="website" class="block text-sm font-medium leading-6 text-gray-900">
                Website
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field name="website" v-model="formData.website_id" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="websiteList"
                      @option:selected="updateWebsite"
                      @option:deselected="updateWebsite"
                      :clearable="false"
                      label="domain" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="website" />
              </div>
            </div>

            <div class="sm:col-span-5" v-if="isAdmin">
              <label for="network" class="block text-sm font-medium leading-6 text-gray-900">
                Network
              </label>
              <div class="mt-2">
                <Field name="network" v-model="formData.network_id" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="networkList"
                      @option:selected="updateNetwork"
                      @option:deselected="updateNetwork"
                      :clearable="false"
                      label="name" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="network" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="parent" class="block text-sm font-medium leading-6 text-gray-900">
                Regulatory Key
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <Field name="parent" v-model="formData.parent_id" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      :reduce="item => item.id"
                      :options="regulatoryKeyList"
                      :clearable="false"
                      label="title" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="parent" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="subtitle" class="block text-sm font-medium leading-6 text-gray-900">Subtitle</label>
              <div class="mt-2">
                <Field v-model="formData.subtitle" v-bind:class="{ 'ring-red-600': errors.subtitle }" type="text" name="subtitle" id="subtitle" placeholder="Subtitle" class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                <ErrorMessage class="mt-2 text-xs text-red-600" name="subtitle" />
              </div>
            </div>

            <div class="col-span-full">
              <label for="text" class="block text-sm font-medium leading-6 text-gray-900">Text</label>
              <div class="mt-2">
                <TrixComponent v-if="editorView" :id="'text'" :text="formData.text" @dataFromTrix="getDataTextFromTrix"></TrixComponent>
                <Field v-model="formData.text" v-bind:class="{ 'ring-red-600': errors.text }" as="textarea" name="text" id="text" hidden="hidden"/>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="text" />
              </div>
              <p class="mt-3 text-sm leading-6 text-gray-600">Text Prompt</p>
            </div>

            <div class="col-span-full">
              <label for="dashboard_content" class="block text-sm font-medium leading-6 text-gray-900">
                Dashboard content
                <span class="text-red-600 text-sm">*</span>
              </label>
              <div class="mt-2">
                <TrixComponent v-if="editorView" :id="'dashboard_content'" :text="formData.dashboard_content" @dataFromTrix="getDataDashboardContentFromTrix"></TrixComponent>
                <Field v-model="formData.dashboard_content" v-bind:class="{ 'ring-red-600': errors.dashboard_content }" as="textarea" name="dashboard_content" id="dashboard_content" hidden="hidden"/>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="dashboard_content" />
              </div>
              <p class="mt-3 text-sm leading-6 text-gray-600">Text Prompt</p>
            </div>

            <div class="sm:col-span-5">
              <label for="regulatory_permissions" class="block text-sm font-medium leading-6 text-gray-900">
                Regulatory Permissions
              </label>
              <div class="mt-2">
                <Field name="regulatory_permissions" v-model="formData.regulatory_permissions" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      multiple
                      :options="regulatoryPermissionsList"
                      label="name" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="regulatory_permissions" />
              </div>
            </div>

            <div class="sm:col-span-5">
              <label for="firm_types" class="block text-sm font-medium leading-6 text-gray-900">
                Firm Types
              </label>
              <div class="mt-2">
                <Field name="firm_types" v-model="formData.firm_types" v-slot="{ componentField }">
                  <v-select
                      v-bind="componentField"
                      multiple
                      :options="firmTypesList"
                      label="name" />
                </Field>
                <ErrorMessage class="mt-2 text-xs text-red-600" name="firm_types" />
              </div>
            </div>

          </div>
        </div>
        <div class="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
          <button @click="cancel" type="button" class="text-sm font-semibold leading-6 text-gray-900">Cancel</button>
          <button v-if="questionSectionId" @click="handleSubmit($event, update)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Update</button>
          <button v-else @click="handleSubmit($event, create)" type="button" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">Save</button>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import { TrashIcon } from '@heroicons/vue/24/solid'
import TrixComponent from "../TrixComponent.vue";

export default {
  name: "QuestionSectionCreateEditFormComponent",

  props: {
    questionSectionId: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      regulatoryKeyList: [],
      regulatoryPermissionsList: [],
      firmTypesList: [],
      websiteList: [],
      networkList: [{id: null, name: '---'}],
      formData: {
        parent_id: null,
        website_id: 1,
        network_id: null,
        title: null,
        subtitle: null,
        text: null,
        dashboard_content: null,
        regulatory_permissions: [],
        firm_types: [],
      },
      editorView: false,
    }
  },
  components: {
    TrixComponent,
    TrashIcon,
  },
  computed: {
    schema() {
      if (this.isAdmin) {
        return yup.object({
          parent: yup.number().required().label('Regulatory Key'),
          website: yup.number().required().label('Website'),
          network: yup.mixed().nullable().label('Network'),
          title: yup.string().required().min(3).label('Title'),
          subtitle: yup.string().nullable().label('Subtitle'),
          text: yup.string().nullable().label('Text'),
          dashboard_content: yup.string().required().min(3).label('Dashboard content'),
          regulatory_permissions: yup.array().min(1).label('Regulatory Permissions'),
          firm_types: yup.array().min(1).label('Firm Types'),
        });
      } else {
        return yup.object({
          parent: yup.number().required().label('Regulatory Key'),
          title: yup.string().required().min(3).label('Title'),
          subtitle: yup.string().nullable().label('Subtitle'),
          text: yup.string().nullable().label('Text'),
          dashboard_content: yup.string().required().min(3).label('Dashboard content'),
          regulatory_permissions: yup.array().min(1).label('Regulatory Permissions'),
          firm_types: yup.array().min(1).label('Firm Types'),
        });
      }
    },
    isAdmin() {
      return this.$store.state.userIsAccountLevelAdmin;
    },
  },
  methods: {
    getRegulatoryKeyList() {
      let networkId = this.formData.network_id ? (`/${this.formData.network_id}`) : '';
      axios.get(`/member-firm/question-section/form-data/regulatory-key-list/${this.formData.website_id}${networkId}`).then((res) => {
        this.regulatoryKeyList = res.data.data;
        if (this.formData.parent_id == null) {
          if (this.regulatoryKeyList.length > 0) {
            this.formData.parent_id = this.regulatoryKeyList[0].id;
          }
        }
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getRegulatoryPermissionsList() {
      axios.get(`/member-firm/question-section/form-data/regulatory-permissions-list`).then((res) => {
        this.regulatoryPermissionsList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getFirmTypesList() {
      axios.get(`/member-firm/question-section/form-data/firm-types-list`).then((res) => {
        this.firmTypesList = res.data.data;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    getWebsiteList() {
      axios.get(`/member-firm/question-section/form-data/website-list`).then((res) => {
        this.websiteList = res.data.data;
        this.getRegulatoryKeyList();
      }).catch((error) => {
        console.log('error', error)
      })
    },
    updateWebsite() {
      this.formData.network_id = null;
      this.formData.parent_id = null;
      this.getNetworkList();
    },
    getNetworkList() {
      axios.get(`/member-firm/question-section/form-data/network-list/${this.formData.website_id}`).then((res) => {
        this.networkList = [{id: null, name: '---'}, ...res.data.data];
        this.getRegulatoryKeyList();
      }).catch((error) => {
        console.log('error', error)
      })
    },
    updateNetwork() {
      this.formData.parent_id = null;
      this.getRegulatoryKeyList();
    },
    getSection() {
      axios.get(`/member-firm/question-section/edit/${this.questionSectionId}`).then((res) => {
        this.formData = res.data.data;
        this.editorView = true;
        this.getRegulatoryKeyList();
        // for admin
        if (this.isAdmin) {
          this.getWebsiteList();
          this.getNetworkList();
        }
      }).catch((error) => {
        console.log('error', error)
      })
    },
    update() {
      axios.put(`/member-firm/question-section/update/${this.questionSectionId}`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            this.$emit('cancel');
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    create() {
      axios.post(`/member-firm/question-section/store`, this.formData)
          .then((res) => {
            this.$emit('reloadIndex');
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            this.$emit('cancel');
          })
          .catch((error) => {
            console.log('error', error);
            this.$emit('reloadIndex');
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    cancel() {
      this.$emit('cancel');
    },
    destroy() {
      if(confirm("Do you really want to delete?")) {
        axios.delete(`/member-firm/question-section/destroy/${this.questionSectionId}`).then((res) => {
          this.$emit('cancel');
          this.$emit('reloadIndex');
          this.$notify({ type: "success", title: "Successfully", text: res.data.message });
        }).catch((error) => {
          console.log('error', error)
          this.$notify({ title: "Unsuccessfully", text: error.message });
          this.$emit('reloadIndex');
        })
      }
    },
    getDataTextFromTrix(data) {
      this.formData.text = data;
    },
    getDataDashboardContentFromTrix(data) {
      this.formData.dashboard_content = data;
    },
  },
  created() {
    if(this.questionSectionId){
      this.getSection();
    } else {
      this.editorView = true;
      this.getRegulatoryKeyList();
      // for admin
      if (this.isAdmin) {
        this.getWebsiteList();
        this.getNetworkList();
      }
    }
    this.getRegulatoryPermissionsList();
    this.getFirmTypesList();
  }
}
</script>

<style scoped>
  @import 'vue-select/dist/vue-select.css';
</style>